import React from 'react';
import { DayPicker, DayPickerProps } from 'react-day-picker';
import dayjs, { Dayjs } from 'dayjs';
import {
    COMMON_DAY_PICKER_PROPS,
    DayPickerField,
    buildBoundMatchers,
    useDatePickerPopoverState,
    useDayPickerLocale,
    useDropdownMonthRange,
} from './datePickerShared';
import 'react-day-picker/style.css';

export interface SingleDatePickerProps {
    value: Dayjs | null;
    onChange?: (date: Dayjs | null) => void;
    // Floating label, MUI-input style. Omit when the field already sits
    // next to its own text label (e.g. inline radio rows). When omitted,
    // `placeholder` is rendered centered until a value is chosen.
    label?: string;
    placeholder?: string;
    disabled?: boolean;
    disablePast?: boolean;
    disableFuture?: boolean;
    // Allowlist of selectable days in YYYY-MM-DD. When set, only listed days
    // are enabled. Wrappers translate any inbound format to YYYY-MM-DD.
    allowedDays?: string[];
    // Optional explicit min/max bounds, in addition to disablePast/disableFuture.
    minDate?: Dayjs;
    maxDate?: Dayjs;
    className?: string;
}

// Shared single-date picker built on react-day-picker with a dropdown
// month/year caption — same UX as the dashboard date-range picker. Trigger
// matches the look of our floating-label inputs so callsites that swapped
// from the old MUI picker render without any visual jump.
//
// API stays in Dayjs so the existing wrappers don't have to touch their
// callsites. The dayjs ↔ Date conversion at the boundary normalises to
// start-of-day to avoid timezone shifts on all-day dates.
export function SingleDatePicker(props: SingleDatePickerProps) {
    const state = useDatePickerPopoverState(props.disabled);

    const locale = useDayPickerLocale();

    const allowedSet = React.useMemo(() => {
        if (!props.allowedDays?.length) return null;
        return new Set(props.allowedDays);
    }, [props.allowedDays]);

    // Base bound matchers plus the optional allow-list of selectable days.
    const disabledMatchers = React.useMemo(() => {
        const matchers = buildBoundMatchers(props);
        if (allowedSet) {
            matchers.push((date: Date) => !allowedSet.has(dayjs(date).format('YYYY-MM-DD')));
        }
        return matchers;
    }, [props.disablePast, props.disableFuture, props.minDate, props.maxDate, allowedSet]);

    // Year-dropdown range: 10 years back for historical edits, 5 forward.
    const { startMonth: dropdownStart, endMonth: dropdownEnd } =
        useDropdownMonthRange(props.minDate, props.maxDate, 10, 5);

    const handleSelect = (date: Date | undefined) => {
        const next = date ? dayjs(date).startOf('day') : null;
        props.onChange?.(next);
        state.close();
    };

    const formatted = props.value ? props.value.format('DD MMM YYYY') : '';
    const triggerText = formatted || (!props.label && props.placeholder) || ' ';

    return (
        <DayPickerField
            state={state}
            text={triggerText}
            hasValue={!!formatted}
            label={props.label}
            disabled={props.disabled}
            className={props.className}
        >

            <DayPicker
                {...({
                    mode: 'single',
                    selected: props.value ? props.value.toDate() : undefined,
                    onSelect: handleSelect,
                    locale,
                    disabled: disabledMatchers,
                    startMonth: dropdownStart,
                    endMonth: dropdownEnd,
                    ...COMMON_DAY_PICKER_PROPS,
                } as DayPickerProps)}
            />
        </DayPickerField>
    );
}

export default SingleDatePicker;
